import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarRange,
  Loader2,
  RefreshCw,
} from 'lucide-react'
import { DashboardCard } from '../Dashboard'
import { useAuth } from '../../contexts/AuthContext'
import { useProfitabilityPwfData } from '../../hooks/useProfitabilityPwfData'
import {
  buildProfitabilityMatrixProjects,
  profitabilityMatrixStats,
  filterProjectsStartedInRange,
  MATRIX_TASK_STARTED_FROM,
  PROJECT_PROFITABILITY_STARTED_FROM,
  formatDurationHhMm,
  PROJECT_LABOUR_RATE_PER_HOUR,
  type ActiveTaskEntry,
  type TimeLogEntry,
} from './mockData'
import { ProjectProfitabilityMatrix } from './ProjectProfitabilityMatrix'
import { ClientProfitabilityMatrix } from './ClientProfitabilityMatrix'
import { ClientProfitabilitySummary } from './ClientProfitabilitySummary'

type ProfitabilityView = 'matrix' | 'clientMatrix' | 'clients'

type SortDir = 'asc' | 'desc'

type EmpSortKey =
  | 'name'
  | 'completedTasks'
  | 'totalRevenue'
  | 'recordedHours'
  | 'allocatedHours'
  | 'recordedMinusAllocated'

const currencyDetailed = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n)

/** Hours variance: recorded time minus allocated time (same units as the hour columns). */
function recordedMinusAllocatedHours(recordedHours: number, allocatedHours: number): number {
  return Math.round((recordedHours - allocatedHours) * 10) / 10
}

function formatTeamTimeSpentHours(hours: number): string {
  if (hours <= 0) return '0:00'
  return formatDurationHhMm(Math.round(hours * 60))
}

/** Time spent − allocated: negative = under allocation (good), positive = over (watch). */
function timeVarianceColorClass(delta: number): string {
  if (delta < 0) return 'text-emerald-700'
  if (delta > 0) return 'text-amber-700'
  return 'text-gray-700'
}

type TimeVarianceDisplay = {
  label: string
  title: string
  colorClass: string
}

function formatTimeVarianceDisplay(
  recordedHours: number,
  allocatedHours: number
): TimeVarianceDisplay {
  if (recordedHours <= 0 && allocatedHours > 0) {
    return {
      label: '—',
      title:
        'Allocated hours on open or completed tasks, but no time logged in this period — not under budget.',
      colorClass: 'text-gray-400',
    }
  }
  if (recordedHours <= 0 && allocatedHours <= 0) {
    return {
      label: '0',
      title: 'No time or allocation in this period.',
      colorClass: 'text-gray-700',
    }
  }
  const delta = recordedMinusAllocatedHours(recordedHours, allocatedHours)
  return {
    label: `${delta > 0 ? '+' : ''}${delta}`,
    title: `Time spent ${recordedHours} h − allocated ${allocatedHours} h. Negative = under allocation (good).`,
    colorClass: timeVarianceColorClass(delta),
  }
}

function normalizeEmployeeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ')
}

function taskAssigneeContactIds(task: {
  contactId?: number | null
  assigneeContactIds?: number[]
}): number[] {
  if (task.assigneeContactIds?.length) return task.assigneeContactIds
  if (task.contactId != null) return [task.contactId]
  return []
}

function taskMatchesStaff(
  task: { employeeName: string; contactId?: number | null; assigneeContactIds?: number[] },
  staffContactId: number | undefined,
  staffName: string
): boolean {
  const nameMatches =
    normalizeEmployeeName(task.employeeName) === normalizeEmployeeName(staffName)
  if (staffContactId != null) {
    const ids = taskAssigneeContactIds(task)
    if (ids.length > 0) return ids.includes(staffContactId) || nameMatches
  }
  return nameMatches
}

function countCompletedTasksForStaff(
  tasks: readonly TimeLogEntry[],
  staffContactId: number | undefined,
  staffName: string
): number {
  let n = 0
  for (const t of tasks) {
    if (taskMatchesStaff(t, staffContactId, staffName)) n += 1
  }
  return n
}

/** True when PWF has time logged on the task (allocated hours only count in that case). */
function taskHasRecordedTime(task: {
  recordedHours?: number
  hoursWorked?: number
  timeRecordedHours?: number
}): boolean {
  const recorded =
    task.timeRecordedHours ?? Math.max(task.recordedHours ?? 0, task.hoursWorked ?? 0)
  return recorded > 0
}

function sumAllocatedHoursForStaff(
  completedTasks: readonly TimeLogEntry[],
  activeTasks: readonly ActiveTaskEntry[],
  staffContactId: number | undefined,
  staffName: string
): number {
  let total = 0
  for (const t of completedTasks) {
    if (taskMatchesStaff(t, staffContactId, staffName) && taskHasRecordedTime(t)) {
      total += t.allocatedHours ?? 0
    }
  }
  for (const t of activeTasks) {
    if (taskMatchesStaff(t, staffContactId, staffName) && taskHasRecordedTime(t)) {
      total += t.allocatedHours ?? 0
    }
  }
  return Math.round(total * 10) / 10
}

/** Display ISO yyyy-mm-dd as dd.mm.yy */
function EmployeeCompletedTasksBreakdown({
  employeeName,
  tasks,
}: {
  employeeName: string
  tasks: TimeLogEntry[]
}) {
  if (tasks.length === 0) {
    return <p className="text-xs text-gray-500">No completed tasks in this date range.</p>
  }

  let sumRevenue = 0
  let sumRecorded = 0
  let sumAllocated = 0
  for (const t of tasks) {
    sumRevenue += t.revenueGenerated
    sumRecorded += t.recordedHours
    if (taskHasRecordedTime(t)) {
      sumAllocated += t.allocatedHours
    }
  }

  return (
    <div className="rounded-lg border border-dashboard-lavender/30 bg-white">
      <p className="border-b border-gray-100 px-3 py-2 text-xs font-semibold text-gray-800">
        {employeeName} — {tasks.length} completed task{tasks.length === 1 ? '' : 's'}
        <span className="ml-2 font-normal text-gray-500">
          Total {currencyDetailed(Math.round(sumRevenue * 100) / 100)}
          <span className="text-gray-400"> · </span>
          {Math.round(sumRecorded * 10) / 10} h time spent
          <span className="text-gray-400"> · </span>
          {Math.round(sumAllocated * 10) / 10} h allocated
        </span>
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-100 text-xs">
          <thead className="bg-gray-50/80">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                Project
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                Task
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">
                Client
              </th>
              <th className="px-3 py-2 text-right font-medium text-gray-500 uppercase tracking-wider">
                Price
              </th>
              <th className="px-3 py-2 text-right font-medium text-gray-500 uppercase tracking-wider">
                Time Spent
              </th>
              <th className="px-3 py-2 text-right font-medium text-gray-500 uppercase tracking-wider">
                Allocated
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {tasks.map((task) => (
              <tr key={task.id} className="hover:bg-gray-50/60">
                <td className="px-3 py-2 text-gray-700 tabular-nums whitespace-nowrap">
                  {formatIsoDateDdMmYy(task.date)}
                </td>
                <td className="px-3 py-2 font-mono text-gray-800">{task.projectNumber}</td>
                <td className="px-3 py-2 text-gray-900">{task.taskName}</td>
                <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{task.projectType}</td>
                <td className="px-3 py-2 text-gray-700">{task.client}</td>
                <td className="px-3 py-2 text-right text-gray-900 tabular-nums">
                  {currencyDetailed(task.revenueGenerated)}
                </td>
                <td className="px-3 py-2 text-right text-gray-700 tabular-nums">{task.recordedHours}</td>
                <td className="px-3 py-2 text-right text-gray-700 tabular-nums">
                  {taskHasRecordedTime(task) ? task.allocatedHours : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function formatIsoDateDdMmYy(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  if (!m) return iso
  const [, y, mo, d] = m
  return `${d}.${mo}.${y.slice(2)}`
}

function defaultDirForEmpKey(key: EmpSortKey): SortDir {
  if (key === 'name') return 'asc'
  return 'desc'
}

function includesCI(haystack: string, needle: string): boolean {
  const n = needle.trim().toLowerCase()
  if (!n) return true
  return haystack.toLowerCase().includes(n)
}

function toYyyyMmDd(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** First day of current calendar month through today (inclusive). */
function getMonthToDateRange(): { from: string; to: string } {
  const to = new Date()
  const from = new Date(to.getFullYear(), to.getMonth(), 1)
  return { from: toYyyyMmDd(from), to: toYyyyMmDd(to) }
}

type DateRangePreset = 'mtd' | 'custom'

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  const Icon = active ? (dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown
  return (
    <Icon
      className={`h-3.5 w-3.5 shrink-0 ${
        active ? 'text-dashboard-terracotta' : 'text-gray-400 opacity-60 group-hover:opacity-100'
      }`}
      aria-hidden
    />
  )
}

function ComingSoonColumnWatermark() {
  return (
    <div
      className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden"
      aria-hidden
    >
      <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-gray-300 -rotate-[-25deg] whitespace-nowrap select-none">
        Coming soon
      </span>
    </div>
  )
}

function SortableTh({
  label,
  sortKey,
  currentKey,
  dir,
  align = 'left',
  title,
  onSort,
}: {
  label: string
  sortKey: string
  currentKey: string
  dir: SortDir
  align?: 'left' | 'right'
  title?: string
  onSort: (key: string) => void
}) {
  const active = currentKey === sortKey
  return (
    <th
      scope="col"
      title={title}
      className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wider ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`group inline-flex items-center gap-1 text-gray-500 hover:text-gray-900 transition-colors ${
          align === 'right' ? 'justify-end w-full' : ''
        }`}
      >
        <span>{label}</span>
        <SortIcon active={active} dir={dir} />
      </button>
    </th>
  )
}

const headerFilterInputClass =
  'w-full min-w-[4.5rem] rounded border border-gray-200 bg-white px-2 py-1 text-xs font-normal normal-case tracking-normal text-gray-900 placeholder:text-gray-400 focus:border-dashboard-lavender focus:outline-none focus:ring-1 focus:ring-dashboard-lavender/40'

const labourRateInputClass =
  'w-16 rounded border border-gray-200 bg-white px-1.5 py-0.5 text-xs font-normal normal-case tracking-normal text-gray-900 tabular-nums text-right focus:border-dashboard-lavender focus:outline-none focus:ring-1 focus:ring-dashboard-lavender/40'

type DateCompletedFilterPanelProps = {
  idPrefix: string
  caption?: string
  /** When true, sits inside another card (above a table): tighter spacing, no outer shadow. */
  embedded?: boolean
  datePreset: DateRangePreset
  dateRange: { from: string; to: string }
  onPresetChange: (preset: DateRangePreset) => void
  onFromChange: (value: string) => void
  onToChange: (value: string) => void
}

function DateCompletedFilterPanel({
  idPrefix,
  caption,
  embedded = false,
  datePreset,
  dateRange,
  onPresetChange,
  onFromChange,
  onToChange,
}: DateCompletedFilterPanelProps) {
  const headingId = `${idPrefix}-date-filter-heading`
  const outerClass = embedded
    ? 'mb-4 rounded-lg border border-gray-200 bg-gray-50/90 p-3'
    : 'mt-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm'
  return (
    <div className={outerClass}>
      {caption ? <p className="mb-3 text-xs text-gray-600">{caption}</p> : null}
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex min-w-[10rem] flex-col gap-1">
          <span className="text-xs font-medium uppercase tracking-wider text-gray-500">Date completed</span>
          <div className="flex items-center gap-2">
            <CalendarRange className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
            <label htmlFor={`${idPrefix}-date-preset`} className="sr-only">
              Date range preset
            </label>
            <select
              id={`${idPrefix}-date-preset`}
              value={datePreset}
              onChange={(e) => onPresetChange(e.target.value as DateRangePreset)}
              className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-dashboard-mint"
            >
              <option value="mtd">Month to date</option>
              <option value="custom">Custom</option>
            </select>
          </div>
        </div>
        {datePreset === 'custom' ? (
          <div
            role="group"
            aria-labelledby={headingId}
            className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-gray-50/90 px-3 py-2.5"
          >
            <p id={headingId} className="text-xs font-semibold uppercase tracking-wider text-gray-600">
              Date filter
            </p>
            <div className="mt-2 flex flex-wrap items-end gap-4">
              <div className="flex min-w-[9.5rem] flex-col gap-1">
                <label htmlFor={`${idPrefix}-date-from`} className="text-xs font-medium text-gray-600">
                  Start date
                </label>
                <input
                  id={`${idPrefix}-date-from`}
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => onFromChange(e.target.value)}
                  aria-label="Date filter start date"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-dashboard-mint focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-dashboard-mint"
                />
              </div>
              <div className="flex min-w-[9.5rem] flex-col gap-1">
                <label htmlFor={`${idPrefix}-date-to`} className="text-xs font-medium text-gray-600">
                  End date
                </label>
                <input
                  id={`${idPrefix}-date-to`}
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => onToChange(e.target.value)}
                  aria-label="Date filter end date"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-dashboard-mint focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-dashboard-mint"
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {datePreset === 'custom' ? (
        <p className={`text-sm text-gray-500 ${embedded ? 'mt-2' : 'mt-3'}`}>
          <span className="font-medium text-gray-600">Date filter:</span> Start date{' '}
          <strong className="font-medium text-gray-800">{formatIsoDateDdMmYy(dateRange.from)}</strong>
          <span className="text-gray-400"> · </span>
          End date{' '}
          <strong className="font-medium text-gray-800">{formatIsoDateDdMmYy(dateRange.to)}</strong>
        </p>
      ) : null}
    </div>
  )
}

function SectionDataBadge({ source }: { source: 'demo' | 'pwf' }) {
  if (source === 'pwf') {
    return (
      <span className="inline-flex items-center rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800">
        PWF snapshot
      </span>
    )
  }
  return (
    <span className="inline-flex items-center rounded-md bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
      Demo data
    </span>
  )
}

export function ProfitabilityDashboard() {
  const { isAdmin } = useAuth()
  const [datePreset, setDatePreset] = useState<DateRangePreset>('mtd')
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>(() => getMonthToDateRange())

  const {
    completedTasks,
    projectProfitability,
    activeTasksInDateRange,
    allActiveTasks,
    loading: snapshotLoading,
    fetchingFromPwf,
    error: snapshotError,
    fetchNotice,
    recordedAt: pwfRecordedAt,
    refetch: reloadSnapshot,
    fetchFromPwf,
    employeeTaskTime,
    allCompletedTasks,
    employeeTimeSpent,
    employeeTimeSpentMtd,
    customTrackedTime,
    customTrackedTimeLoading,
    customTrackedTimeError,
  } = useProfitabilityPwfData(dateRange, datePreset)

  const [empSort, setEmpSort] = useState<{ key: EmpSortKey; dir: SortDir }>({
    key: 'recordedHours',
    dir: 'desc',
  })
  const [empNameFilter, setEmpNameFilter] = useState('')
  const [employeeTasksExpanded, setEmployeeTasksExpanded] = useState<string | null>(null)

  const [projectLabourRatePerHour, setProjectLabourRatePerHour] = useState(PROJECT_LABOUR_RATE_PER_HOUR)
  const [profitabilityView, setProfitabilityView] = useState<ProfitabilityView>('matrix')
  const [matrixClientFilter, setMatrixClientFilter] = useState('')
  const [fetchElapsedSec, setFetchElapsedSec] = useState(0)

  const viewClientProjectsOnMatrix = (client: string) => {
    setMatrixClientFilter(client)
    setProfitabilityView('matrix')
  }

  useEffect(() => {
    if (!fetchingFromPwf) {
      setFetchElapsedSec(0)
      return
    }
    const started = Date.now()
    const timer = window.setInterval(() => {
      setFetchElapsedSec(Math.floor((Date.now() - started) / 1000))
    }, 1000)
    return () => window.clearInterval(timer)
  }, [fetchingFromPwf])

  useEffect(() => {
    setEmployeeTasksExpanded(null)
  }, [dateRange.from, dateRange.to])

  const logsInDateRange = completedTasks
  const filtered = logsInDateRange

  const employeeTimeSpentMtdByName = useMemo(() => {
    const map = new Map<string, number>()
    for (const row of employeeTimeSpentMtd) {
      if (!row.name || row.name === '—') continue
      map.set(normalizeEmployeeName(row.name), row.recordedHours)
    }
    return map
  }, [employeeTimeSpentMtd])

  const customTrackedTimeByName = useMemo(() => {
    const map = new Map<string, number>()
    for (const row of customTrackedTime) {
      if (!row.name || row.name === '—') continue
      map.set(normalizeEmployeeName(row.name), row.recordedHours)
    }
    return map
  }, [customTrackedTime])

  const teamTimeSpentPeriodLabel = useMemo(() => {
    if (datePreset === 'mtd') {
      return `Hours tracked ${formatIsoDateDdMmYy(dateRange.from)} – ${formatIsoDateDdMmYy(dateRange.to)} (PWF snapshot)`
    }
    if (customTrackedTimeLoading) {
      return `Loading hours tracked ${formatIsoDateDdMmYy(dateRange.from)} – ${formatIsoDateDdMmYy(dateRange.to)}…`
    }
    return `Hours tracked ${formatIsoDateDdMmYy(dateRange.from)} – ${formatIsoDateDdMmYy(dateRange.to)}`
  }, [datePreset, dateRange.from, dateRange.to, customTrackedTimeLoading])

  function resolveTeamTimeSpentHours(employeeName: string): number {
    const key = normalizeEmployeeName(employeeName)
    if (datePreset === 'mtd') {
      return employeeTimeSpentMtdByName.get(key) ?? 0
    }
    return customTrackedTimeByName.get(key) ?? 0
  }

  const employeeNames = useMemo(() => {
    const names = new Set<string>()
    for (const t of completedTasks) {
      if (t.employeeName && t.employeeName !== '—') names.add(t.employeeName)
    }
    for (const t of activeTasksInDateRange) {
      if (t.employeeName && t.employeeName !== '—') names.add(t.employeeName)
    }
    for (const row of employeeTaskTime) {
      if (!row.name || row.name === '—') continue
      if (row.recordedHours > 0) names.add(row.name)
    }
    for (const row of employeeTimeSpent) {
      if (row.name && row.name !== '—' && row.recordedHours > 0) {
        names.add(row.name)
      }
    }
    for (const row of employeeTimeSpentMtd) {
      if (row.name && row.name !== '—' && row.recordedHours > 0) {
        names.add(row.name)
      }
    }
    for (const row of customTrackedTime) {
      if (row.name && row.name !== '—' && row.recordedHours > 0) {
        names.add(row.name)
      }
    }
    return [...names].sort((a, b) => a.localeCompare(b))
  }, [
    completedTasks,
    activeTasksInDateRange,
    employeeTaskTime,
    employeeTimeSpent,
    employeeTimeSpentMtd,
    customTrackedTime,
  ])

  const staffContactIdByName = useMemo(() => {
    const map = new Map<string, number>()
    for (const row of employeeTimeSpent) {
      if (row.name && row.name !== '—') {
        map.set(normalizeEmployeeName(row.name), row.contactId)
      }
    }
    for (const row of employeeTimeSpentMtd) {
      if (row.name && row.name !== '—') {
        map.set(normalizeEmployeeName(row.name), row.contactId)
      }
    }
    for (const t of allCompletedTasks) {
      if (t.employeeName && t.contactId != null) {
        map.set(normalizeEmployeeName(t.employeeName), t.contactId)
      }
    }
    for (const t of activeTasksInDateRange) {
      if (!t.employeeName || t.employeeName === '—') continue
      const key = normalizeEmployeeName(t.employeeName)
      if (map.has(key)) continue
      const contactId = t.contactId ?? t.assigneeContactIds?.[0]
      if (contactId != null) map.set(key, contactId)
    }
    return map
  }, [employeeTimeSpent, employeeTimeSpentMtd, allCompletedTasks, activeTasksInDateRange])

  const employeeBaseRows = useMemo(() => {
    const byName = new Map<
      string,
      {
        name: string
        totalRevenue: number
      }
    >()
    for (const e of filtered) {
      const cur = byName.get(e.employeeName) ?? {
        name: e.employeeName,
        totalRevenue: 0,
      }
      cur.totalRevenue = Math.round((cur.totalRevenue + e.revenueGenerated) * 100) / 100
      byName.set(e.employeeName, cur)
    }
    const rows = employeeNames.map((name) => {
      const staffContactId = staffContactIdByName.get(normalizeEmployeeName(name))
      const recordedHours = Math.round(resolveTeamTimeSpentHours(name) * 10) / 10
      const completedTasks = countCompletedTasksForStaff(filtered, staffContactId, name)
      const allocatedHours = sumAllocatedHoursForStaff(
        filtered,
        activeTasksInDateRange,
        staffContactId,
        name
      )
      const existing = byName.get(name)
      if (existing) {
        return {
          name,
          completedTasks,
          totalRevenue: existing.totalRevenue,
          recordedHours,
          allocatedHours,
          recordedMinusAllocated: recordedMinusAllocatedHours(recordedHours, allocatedHours),
        }
      }
      return {
        name,
        completedTasks,
        totalRevenue: 0,
        recordedHours,
        allocatedHours,
        recordedMinusAllocated: recordedMinusAllocatedHours(recordedHours, allocatedHours),
      }
    })
    return rows
  }, [
    filtered,
    activeTasksInDateRange,
    employeeNames,
    datePreset,
    employeeTimeSpentMtdByName,
    customTrackedTimeByName,
    staffContactIdByName,
  ])

  const employeeFiltered = useMemo(
    () => employeeBaseRows.filter((r) => includesCI(r.name, empNameFilter)),
    [employeeBaseRows, empNameFilter]
  )

  const completedTasksByEmployee = useMemo(() => {
    const map = new Map<string, TimeLogEntry[]>()
    for (const name of employeeNames) {
      const staffContactId = staffContactIdByName.get(normalizeEmployeeName(name))
      const tasks = filtered.filter((t) => taskMatchesStaff(t, staffContactId, name))
      if (tasks.length > 0) {
        tasks.sort((a, b) => b.date.localeCompare(a.date) || a.id.localeCompare(b.id))
        map.set(name, tasks)
      }
    }
    return map
  }, [filtered, employeeNames, staffContactIdByName])

  const employeeDisplayRows = useMemo(() => {
    const rows = [...employeeFiltered]
    const d = empSort.dir === 'asc' ? 1 : -1
    rows.sort((a, b) => {
      let cmp = 0
      switch (empSort.key) {
        case 'name':
          cmp = a.name.localeCompare(b.name)
          break
        case 'completedTasks':
          cmp = a.completedTasks - b.completedTasks
          break
        case 'totalRevenue':
          cmp = a.totalRevenue - b.totalRevenue
          break
        case 'recordedHours':
          cmp = a.recordedHours - b.recordedHours
          break
        case 'allocatedHours':
          cmp = a.allocatedHours - b.allocatedHours
          break
        case 'recordedMinusAllocated':
          cmp = a.recordedMinusAllocated - b.recordedMinusAllocated
          break
        default:
          cmp = 0
      }
      if (cmp === 0) cmp = a.name.localeCompare(b.name)
      return cmp * d
    })
    return rows
  }, [employeeFiltered, empSort])

  const projectProfitabilityEligible = useMemo(
    () =>
      filterProjectsStartedInRange(
        projectProfitability,
        PROJECT_PROFITABILITY_STARTED_FROM,
        ''
      ),
    [projectProfitability]
  )

  const matrixProjects = useMemo(
    () =>
      buildProfitabilityMatrixProjects(
        allCompletedTasks,
        projectProfitabilityEligible,
        allActiveTasks
      ),
    [allCompletedTasks, projectProfitabilityEligible, allActiveTasks]
  )

  const matrixStats = useMemo(
    () => profitabilityMatrixStats(allCompletedTasks, projectProfitabilityEligible),
    [allCompletedTasks, projectProfitabilityEligible]
  )

  const applyDatePreset = (preset: DateRangePreset) => {
    setDatePreset(preset)
    if (preset === 'mtd') setDateRange(getMonthToDateRange())
    // custom: keep current from/to (initially still month-to-date until user edits)
  }

  const setDateFromInput = (which: 'from' | 'to', value: string) => {
    setDatePreset('custom')
    setDateRange((prev) => {
      let from = prev.from
      let to = prev.to
      if (which === 'from') from = value
      else to = value
      if (from > to) {
        if (which === 'from') to = from
        else from = to
      }
      return { from, to }
    })
  }

  const cycleEmpSort = (key: string) => {
    const k = key as EmpSortKey
    setEmpSort((prev) =>
      prev.key === k ? { key: k, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: defaultDirForEmpKey(k) }
    )
  }

  return (
    <div className="space-y-6">
      {snapshotLoading || fetchingFromPwf ? (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
            {fetchingFromPwf
              ? 'Fetching from ProWorkflow and saving snapshot…'
              : 'Loading profitability snapshot…'}
          </div>
          {fetchingFromPwf ? (
            <p className="mt-1 text-xs text-gray-500">
              Fetching from ProWorkflow — usually 1–3 minutes (tasks, invoices, and time).
              Elapsed: {fetchElapsedSec}s
            </p>
          ) : null}
        </div>
      ) : null}
      {fetchNotice ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          {fetchNotice}
        </div>
      ) : null}
      {snapshotError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {snapshotError}
          <button
            type="button"
            onClick={() => void reloadSnapshot()}
            className="ml-2 font-medium text-red-900 underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {!snapshotLoading && !fetchingFromPwf && !snapshotError && pwfRecordedAt ? (
          <p className="text-xs text-emerald-700">
            PWF snapshot · recorded {pwfRecordedAt}
          </p>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {isAdmin ? (
            <button
              type="button"
              onClick={() => void fetchFromPwf()}
              disabled={fetchingFromPwf}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${fetchingFromPwf ? 'animate-spin' : ''}`} />
              {fetchingFromPwf ? 'Fetching…' : 'Fetch from PWF'}
            </button>
          ) : null}
        </div>
      </div>

      <section className="rounded-xl bg-dashboard-lavender/10 py-6 px-4 sm:px-6 -mx-4 sm:-mx-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex flex-wrap items-center gap-2">
          <span className="w-1 h-6 rounded-full bg-dashboard-lavender" aria-hidden />
          Team performance
          <SectionDataBadge source="pwf" />
        </h2>
        <DashboardCard accent="lavender">
          <DateCompletedFilterPanel
            idPrefix="pp-emp"
            embedded
            caption="Completed tasks filter by completion date. Time spent is hours tracked in PWF for the selected period (custom ranges use the snapshot or load live from PWF)."
            datePreset={datePreset}
            dateRange={dateRange}
            onPresetChange={applyDatePreset}
            onFromChange={(v) => setDateFromInput('from', v)}
            onToChange={(v) => setDateFromInput('to', v)}
          />
          {datePreset === 'custom' && customTrackedTimeError ? (
            <p className="mb-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Could not load tracked time for this date range: {customTrackedTimeError}
            </p>
          ) : null}
          <div className="overflow-x-auto -mx-5 px-5 sm:mx-0 sm:px-0">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh
                    label="Name"
                    sortKey="name"
                    currentKey={empSort.key}
                    dir={empSort.dir}
                    align="left"
                    onSort={cycleEmpSort}
                  />
                  <SortableTh
                    label="Time spent"
                    sortKey="recordedHours"
                    currentKey={empSort.key}
                    dir={empSort.dir}
                    align="right"
                    title={teamTimeSpentPeriodLabel}
                    onSort={cycleEmpSort}
                  />
                  <SortableTh
                    label="Completed tasks"
                    sortKey="completedTasks"
                    currentKey={empSort.key}
                    dir={empSort.dir}
                    align="right"
                    onSort={cycleEmpSort}
                  />
                  <th
                    scope="col"
                    className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-right opacity-60 relative min-w-[6.5rem]"
                  >
                    <span className="block text-gray-500">Total revenue</span>
                    <span className="block text-[10px] font-normal normal-case tracking-normal text-gray-400 mt-0.5">
                      Coming soon
                    </span>
                  </th>
                  <SortableTh
                    label="Allocated (hrs)"
                    sortKey="allocatedHours"
                    currentKey={empSort.key}
                    dir={empSort.dir}
                    align="right"
                    title="Sum of PWF time allocated on tasks with recorded time (completed in range plus open tasks due in the active-task period)"
                    onSort={cycleEmpSort}
                  />
                  <SortableTh
                    label="Over / under alloc. (h)"
                    sortKey="recordedMinusAllocated"
                    currentKey={empSort.key}
                    dir={empSort.dir}
                    align="right"
                    title="Time spent minus allocated hours. Negative = under allocation (good). Positive = over allocation."
                    onSort={cycleEmpSort}
                  />
                </tr>
                <tr className="border-t border-gray-200">
                  <th scope="col" className="px-4 py-2 align-top">
                    <label className="sr-only" htmlFor="emp-filter-name">
                      Filter by name
                    </label>
                    <input
                      id="emp-filter-name"
                      type="search"
                      value={empNameFilter}
                      onChange={(e) => setEmpNameFilter(e.target.value)}
                      placeholder="Filter name…"
                      className={headerFilterInputClass}
                    />
                  </th>
                  <th colSpan={5} className="px-4 py-2 text-right text-[10px] font-normal text-gray-400 normal-case tracking-normal">
                    Over / under alloc.: − = under (good) · + = over · — = no time logged
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {employeeDisplayRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No rows for this filter.
                    </td>
                  </tr>
                ) : (
                  employeeDisplayRows.map((row) => {
                    const isExpanded = employeeTasksExpanded === row.name
                    const employeeTasks = completedTasksByEmployee.get(row.name) ?? []
                    const timeVariance = formatTimeVarianceDisplay(
                      row.recordedHours,
                      row.allocatedHours
                    )
                    return (
                      <Fragment key={row.name}>
                        <tr
                          className={`hover:bg-gray-50/50 ${isExpanded ? 'bg-dashboard-lavender/5' : ''}`}
                        >
                          <td className="px-4 py-2.5 text-gray-900 font-medium">{row.name}</td>
                          <td
                            className="px-4 py-2.5 text-right text-gray-700 tabular-nums"
                            title={teamTimeSpentPeriodLabel}
                          >
                            {datePreset === 'custom' && customTrackedTimeLoading
                              ? '…'
                              : formatTeamTimeSpentHours(row.recordedHours)}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums">
                            {row.completedTasks > 0 ? (
                              <button
                                type="button"
                                onClick={() =>
                                  setEmployeeTasksExpanded((prev) =>
                                    prev === row.name ? null : row.name
                                  )
                                }
                                className={`font-medium text-dashboard-terracotta hover:underline ${
                                  isExpanded ? 'underline' : ''
                                }`}
                                aria-expanded={isExpanded}
                                aria-label={`${isExpanded ? 'Hide' : 'Show'} completed tasks for ${row.name}`}
                              >
                                {row.completedTasks}
                              </button>
                            ) : (
                              <span className="text-gray-400">0</span>
                            )}
                          </td>
                          <td
                            className="px-4 py-2.5 text-right text-gray-900 tabular-nums relative min-w-[6.5rem] opacity-60"
                            aria-label="Total revenue — coming soon"
                          >
                            <span className="invisible" aria-hidden>
                              {currencyDetailed(row.totalRevenue)}
                            </span>
                            <ComingSoonColumnWatermark />
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-700 tabular-nums">{row.allocatedHours}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums">
                            <span className={timeVariance.colorClass} title={timeVariance.title}>
                              {timeVariance.label}
                            </span>
                          </td>
                        </tr>
                        {isExpanded ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-3 bg-gray-50/80 border-t border-gray-100">
                              <EmployeeCompletedTasksBreakdown
                                employeeName={row.name}
                                tasks={employeeTasks}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </DashboardCard>
      </section>

      <section className="rounded-xl bg-dashboard-terracotta/10 py-6 px-4 sm:px-6 -mx-4 sm:-mx-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex flex-wrap items-center gap-2">
          <span className="w-1 h-6 rounded-full bg-dashboard-terracotta" aria-hidden />
          Project Profitability
          <SectionDataBadge source="pwf" />
        </h2>
        <DashboardCard accent="terracotta">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <div className="space-y-3 min-w-0 flex-1">
              <div
                className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5"
                role="tablist"
                aria-label="Profitability view"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={profitabilityView === 'matrix'}
                  onClick={() => setProfitabilityView('matrix')}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    profitabilityView === 'matrix'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Task
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={profitabilityView === 'clientMatrix'}
                  onClick={() => setProfitabilityView('clientMatrix')}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    profitabilityView === 'clientMatrix'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Client
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={profitabilityView === 'clients'}
                  onClick={() => setProfitabilityView('clients')}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    profitabilityView === 'clients'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Detail View
                </button>
              </div>
              <p className="text-xs text-gray-600 max-w-2xl">
                {profitabilityView === 'matrix' ? (
                  <>
                    One dot per invoiced project started on or after{' '}
                    {formatIsoDateDdMmYy(PROJECT_PROFITABILITY_STARTED_FROM)} with tasks completed since{' '}
                    {formatIsoDateDdMmYy(MATRIX_TASK_STARTED_FROM)} (excludes Brevity / BVT).
                  </>
                ) : profitabilityView === 'clientMatrix' ? (
                  <>
                    One dot per client — rolled-up margin and total invoiced (ex GST) across their projects
                    started on or after {formatIsoDateDdMmYy(PROJECT_PROFITABILITY_STARTED_FROM)} (excludes
                    Brevity / BVT). Filter by completion date to include only projects completed in range.
                  </>
                ) : (
                  <>
                    Client-level roll-up of invoiced projects started on or after{' '}
                    {formatIsoDateDdMmYy(PROJECT_PROFITABILITY_STARTED_FROM)} (excludes Brevity / BVT).
                    Totals sum invoice (ex GST), tracked hours, and profit across each client&apos;s projects;
                    margin is weighted by invoiced amount.
                  </>
                )}
              </p>
            </div>
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 shrink-0">
              <span>Labour rate</span>
              <span>$</span>
              <input
                type="number"
                min={0}
                step={1}
                value={projectLabourRatePerHour}
                onChange={(e) => {
                  const value = Number(e.target.value)
                  if (Number.isFinite(value) && value >= 0) {
                    setProjectLabourRatePerHour(value)
                  }
                }}
                className={labourRateInputClass}
                aria-label="Labour rate per hour"
              />
              <span>/hr</span>
            </label>
          </div>
          {profitabilityView === 'matrix' ? (
            <ProjectProfitabilityMatrix
              projects={matrixProjects}
              completedTasks={allCompletedTasks}
              activeTasks={allActiveTasks}
              labourRatePerHour={projectLabourRatePerHour}
              stats={matrixStats}
              clientFilter={matrixClientFilter}
              onClientFilterChange={setMatrixClientFilter}
            />
          ) : profitabilityView === 'clientMatrix' ? (
            <ClientProfitabilityMatrix
              projects={matrixProjects}
              completedTasks={allCompletedTasks}
              activeTasks={allActiveTasks}
              labourRatePerHour={projectLabourRatePerHour}
              onClientClick={viewClientProjectsOnMatrix}
            />
          ) : (
            <ClientProfitabilitySummary
              projects={matrixProjects}
              completedTasks={allCompletedTasks}
              activeTasks={allActiveTasks}
              labourRatePerHour={projectLabourRatePerHour}
            />
          )}
        </DashboardCard>
      </section>
    </div>
  )
}
